import React, { useContext, useRef, useState } from 'react';
import { Menu } from 'primereact/menu';
import { FeaturesEnum } from '@api/ringil-api.v2.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import Tabs, { TabProps } from '@components/ringil3/RingilTabs/Tabs.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useNavigation from '@hooks/useNavigation.tsx';
import TemplatesList from '@app/pages/customer/TemplatesList/TemplatesList.tsx';
import LinesList from '@app/pages/customer/TemplatesList/LinesList.tsx';

const TAB_TEMPLATES = 'tab-templates';
const TAB_LINES = 'tab-lines';

/**
 * "Šablony" page. With CONTRACTED_CARRIERS the old templates list becomes the first of two tabs, next
 * to "Linky" (Template 2.0); without the feature it is the old list exactly as before.
 */
const TemplatesPage: React.FC = () => {
  const { tr } = useTranslationLgs();
  const { hasFeature } = useContext(AuthenticatedUserContext);
  const nav = useNavigation();
  const menuRef = useRef<Menu>(null);
  const buttonRef = useRef<HTMLSpanElement>(null);
  const linesEnabled = hasFeature([FeaturesEnum.CONTRACTED_CARRIERS]);

  const [activeTab, setActiveTab] = useState(() => (new URLSearchParams(window.location.search).get('tab') === TAB_LINES ? 1 : 0));

  if (!linesEnabled) return <TemplatesList />;

  const tabs: TabProps[] = [
    { label: tr('TemplatesList.tabTemplates', 'Šablony'), active: activeTab === 0, anchorId: TAB_TEMPLATES },
    { label: tr('TemplatesList.tabLines', 'Linky'), active: activeTab === 1, anchorId: TAB_LINES },
  ];

  return (
    <div className={'flex flex-col'}>
      {/* same header as the other lists (Seznam rezervací): breadcrumb, then the title with a "+ Nová" link */}
      <BreadCrumbComponent items={[{ label: tr('TemplatesList.heading', 'Šablony') }]} />
      <div className={'flex flex-row items-center'}>
        <Heading title={tr('TemplatesList.heading', 'Šablony')} headingVariant={'h1'} />
        <span ref={buttonRef} className={'inline-flex'}>
          <RingilButton
            mode={'link'}
            icon={<i className={'pi pi-plus'} />}
            label={tr('TemplatesList.new', 'Nová')}
            onClick={() => menuRef.current?.toggle({ currentTarget: buttonRef.current } as unknown as React.SyntheticEvent)}
            dataTestId={'templates-new'}
          />
        </span>
        <Menu
          popup
          ref={menuRef}
          model={[
            {
              label: tr('TemplatesList.newTemplate', 'Šablona'),
              command: () => nav.navigate(nav.urlFunctions.createCustomerExpeditionTemplate()),
            },
            {
              label: tr('TemplatesList.newLine', 'Linka'),
              command: () => nav.navigate(nav.urlFunctions.createCustomerLine()),
            },
          ]}
        />
      </div>
      <Tabs tabs={tabs} onTabChange={setActiveTab} replaceUrl tailSpacer={false} />
      {activeTab === 0 && <TemplatesList hideHeader />}
      {activeTab === 1 && <LinesList />}
    </div>
  );
};

export default TemplatesPage;
